<template>
  <div class="recipe-manager">
    <!-- Header -->
    <div class="manager-header">
      <div class="header-left">
        <h2>Recipe Manager</h2>
        <span class="recipe-count">{{ recipes.length }} recipes</span>
      </div>
      <button class="add-btn" @click="openEditor(null)">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="12" y1="5" x2="12" y2="19"></line>
          <line x1="5" y1="12" x2="19" y2="12"></line>
        </svg>
        Add Recipe
      </button>
    </div>

    <!-- Filters -->
    <div class="filters-row">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search recipes..."
          class="search-input"
        />
      </div>
      <select v-model="typeFilter" class="filter-select">
        <option value="">All Types</option>
        <option v-for="type in mealTypes" :key="type.value" :value="type.value">
          {{ type.label }}
        </option>
      </select>
      <select v-model="contextFilter" class="filter-select">
        <option value="">All Contexts</option>
        <option value="match_day">Match Day</option>
        <option value="training_day">Training Day</option>
        <option value="recovery_day">Recovery Day</option>
        <option value="rest_day">Rest Day</option>
      </select>
      <select v-model="statusFilter" class="filter-select">
        <option value="">All Status</option>
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
      </select>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <span>Loading recipes...</span>
    </div>

    <!-- Recipe list -->
    <div v-else-if="filteredRecipes.length > 0" class="recipe-list">
      <div class="recipe-table">
        <div class="table-header">
          <span class="col-name">Name</span>
          <span class="col-type">Type</span>
          <span class="col-context">Context</span>
          <span class="col-macros">Macros</span>
          <span class="col-status">Status</span>
          <span class="col-actions">Actions</span>
        </div>
        <div
          v-for="recipe in filteredRecipes"
          :key="recipe.id"
          class="table-row"
          :class="{ inactive: !recipe.is_active }"
        >
          <div class="col-name">
            <span class="recipe-name">{{ recipe.name }}</span>
            <span v-if="recipe.category" class="recipe-category">{{ recipe.category }}</span>
          </div>
          <div class="col-type">
            <span class="type-badge" :style="getTypeStyle(recipe.meal_type)">
              {{ getMealTypeLabel(recipe.meal_type) }}
            </span>
          </div>
          <div class="col-context">
            <span v-if="recipe.context" class="context-badge">
              {{ getContextLabel(recipe.context) }}
            </span>
            <span v-else class="context-any">Any</span>
          </div>
          <div class="col-macros">
            <span class="macro-item">C: {{ recipe.carbs || 0 }}g</span>
            <span class="macro-item">P: {{ recipe.protein || 0 }}g</span>
            <span class="macro-item">F: {{ recipe.fat || 0 }}g</span>
          </div>
          <div class="col-status">
            <button
              class="status-toggle"
              :class="{ active: recipe.is_active }"
              @click="toggleStatus(recipe)"
            >
              {{ recipe.is_active ? 'Active' : 'Inactive' }}
            </button>
          </div>
          <div class="col-actions">
            <button class="action-btn edit" @click="openEditor(recipe)" title="Edit">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
            </button>
            <button class="action-btn delete" @click="confirmDelete(recipe)" title="Delete">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="empty-state">
      <div class="empty-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="12" y1="18" x2="12" y2="12"></line>
          <line x1="9" y1="15" x2="15" y2="15"></line>
        </svg>
      </div>
      <h3>No Recipes Found</h3>
      <p v-if="searchQuery || typeFilter || contextFilter">
        No recipes match your current filters.
      </p>
      <p v-else>
        Get started by adding your first recipe.
      </p>
      <button class="add-first-btn" @click="openEditor(null)">
        Add Recipe
      </button>
    </div>

    <!-- Recipe Editor Modal -->
    <RecipeEditorModal
      v-if="showEditor"
      :recipe="selectedRecipe"
      @save="handleSave"
      @close="closeEditor"
    />

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteConfirm" class="modal-overlay" @click="closeDeleteConfirm">
      <div class="confirm-modal" @click.stop>
        <h3>Delete Recipe?</h3>
        <p>Are you sure you want to delete "{{ recipeToDelete?.name }}"?</p>
        <p class="confirm-note">This will hide the recipe from players but can be reactivated later.</p>
        <div class="confirm-actions">
          <button class="cancel-btn" @click="closeDeleteConfirm">Cancel</button>
          <button class="delete-confirm-btn" @click="deleteRecipe">Delete</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Nutrition } from "@/services/nutrition-service";
import RecipeEditorModal from "./RecipeEditorModal.vue";

export default {
  name: "RecipeManager",

  components: {
    RecipeEditorModal,
  },

  data() {
    return {
      loading: true,
      recipes: [],
      searchQuery: "",
      typeFilter: "",
      contextFilter: "",
      statusFilter: "",
      showEditor: false,
      selectedRecipe: null,
      showDeleteConfirm: false,
      recipeToDelete: null,
      mealTypes: Nutrition.getAllMealTypes(),
    };
  },

  computed: {
    ...mapGetters(["getProfile"]),

    filteredRecipes() {
      let filtered = [...this.recipes];

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(
          (r) =>
            r.name?.toLowerCase().includes(query) ||
            r.description?.toLowerCase().includes(query) ||
            r.ingredients?.toLowerCase().includes(query)
        );
      }

      // Type filter
      if (this.typeFilter) {
        filtered = filtered.filter((r) => r.meal_type === this.typeFilter);
      }

      // Context filter
      if (this.contextFilter) {
        filtered = filtered.filter((r) => r.context === this.contextFilter);
      }

      // Status filter
      if (this.statusFilter) {
        filtered = filtered.filter((r) =>
          this.statusFilter === "active" ? r.is_active : !r.is_active
        );
      }

      return filtered;
    },
  },

  mounted() {
    this.loadRecipes();
  },

  methods: {
    async loadRecipes() {
      this.loading = true;
      try {
        const clubId = this.getProfile?.club_id || this.getProfile?.clubId;
        const response = await Nutrition.getAllMealTemplates(clubId, true);

        if (response.data?.success) {
          this.recipes = response.data.data || [];
        }
      } catch (error) {
        console.error("Error loading recipes:", error);
      } finally {
        this.loading = false;
      }
    },

    openEditor(recipe) {
      this.selectedRecipe = recipe ? { ...recipe } : null;
      this.showEditor = true;
    },

    closeEditor() {
      this.showEditor = false;
      this.selectedRecipe = null;
    },

    async handleSave(recipeData) {
      try {
        let response;
        if (recipeData.id) {
          // Update existing
          response = await Nutrition.updateMealTemplate(recipeData.id, recipeData);
        } else {
          // Create new
          const clubId = this.getProfile?.club_id || this.getProfile?.clubId;
          response = await Nutrition.createMealTemplate({
            ...recipeData,
            club_id: clubId,
          });
        }

        if (response.data?.success) {
          this.$toast?.success?.(recipeData.id ? "Recipe updated" : "Recipe created");
          this.closeEditor();
          await this.loadRecipes();
        } else {
          this.$toast?.error?.(response.data?.error || "Failed to save recipe");
        }
      } catch (error) {
        console.error("Error saving recipe:", error);
        this.$toast?.error?.("Failed to save recipe");
      }
    },

    async toggleStatus(recipe) {
      try {
        const newStatus = !recipe.is_active;
        const response = await Nutrition.toggleTemplateActive(recipe.id, newStatus);

        if (response.data?.success) {
          recipe.is_active = newStatus;
          this.$toast?.success?.(newStatus ? "Recipe activated" : "Recipe deactivated");
        }
      } catch (error) {
        console.error("Error toggling status:", error);
      }
    },

    confirmDelete(recipe) {
      this.recipeToDelete = recipe;
      this.showDeleteConfirm = true;
    },

    closeDeleteConfirm() {
      this.showDeleteConfirm = false;
      this.recipeToDelete = null;
    },

    async deleteRecipe() {
      if (!this.recipeToDelete) return;

      try {
        const response = await Nutrition.deleteMealTemplate(this.recipeToDelete.id);

        if (response.data?.success) {
          this.$toast?.success?.("Recipe deleted");
          this.closeDeleteConfirm();
          await this.loadRecipes();
        }
      } catch (error) {
        console.error("Error deleting recipe:", error);
        this.$toast?.error?.("Failed to delete recipe");
      }
    },

    getMealTypeLabel(type) {
      const info = Nutrition.getMealTypeInfo(type);
      return info.label;
    },

    getTypeStyle(type) {
      const info = Nutrition.getMealTypeInfo(type);
      return {
        backgroundColor: info.color + "20",
        color: info.color,
      };
    },

    getContextLabel(context) {
      const info = Nutrition.getContextInfo(context);
      return info.shortLabel || info.label;
    },
  },
};
</script>

<style scoped lang="scss">
.recipe-manager {
  padding: 16px;
}

.manager-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  .header-left {
    h2 {
      margin: 0;
      font-size: 1.25rem;
      color: #333;
    }

    .recipe-count {
      font-size: 0.85rem;
      color: #666;
    }
  }
}

.add-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #1e7e34;
  }
}

.filters-row {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.search-box {
  flex: 1;
  min-width: 200px;
}

.search-input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
}

.filter-select {
  padding: 10px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;
  cursor: pointer;
  min-width: 120px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #666;

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e9ecef;
    border-top-color: #007bff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.recipe-table {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.table-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1.5fr 80px 80px;
  gap: 12px;
  padding: 12px 16px;
  background: #f8f9fa;
  font-size: 0.8rem;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
}

.table-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1.5fr 80px 80px;
  gap: 12px;
  padding: 14px 16px;
  align-items: center;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.2s;

  &:hover {
    background: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }

  &.inactive {
    opacity: 0.6;
  }
}

.col-name {
  min-width: 0;

  .recipe-name {
    display: block;
    font-weight: 600;
    color: #333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .recipe-category {
    font-size: 0.75rem;
    color: #666;
  }
}

.type-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.context-badge {
  font-size: 0.85rem;
  color: #333;
}

.context-any {
  font-size: 0.85rem;
  color: #999;
}

.col-macros {
  display: flex;
  gap: 8px;
  font-size: 0.8rem;
  color: #666;
}

.status-toggle {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  background: #f8d7da;
  color: #dc3545;

  &.active {
    background: #d4edda;
    color: #28a745;
  }
}

.col-actions {
  display: flex;
  gap: 8px;
}

.action-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;

  // Force icon colour via CSS so it always paints — a bare
  // stroke="currentColor" presentation attribute can be overridden by
  // global/vendor stylesheets, leaving the icon invisible at rest.
  svg {
    fill: none;
    stroke: currentColor;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &.edit {
    background: #e9ecef;
    color: #343a40;

    &:hover {
      background: #007bff;
      color: white;
    }
  }

  &.delete {
    background: #e9ecef;
    color: #343a40;

    &:hover {
      background: #dc3545;
      color: white;
    }
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  .empty-icon {
    color: #ccc;
    margin-bottom: 16px;
  }

  h3 {
    margin: 0 0 8px;
    font-size: 1.1rem;
    color: #333;
  }

  p {
    margin: 0 0 20px;
    color: #666;
    font-size: 0.9rem;
  }
}

.add-first-btn {
  padding: 10px 24px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #1e7e34;
  }
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.confirm-modal {
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 400px;
  width: 100%;

  h3 {
    margin: 0 0 12px;
    font-size: 1.1rem;
    color: #333;
  }

  p {
    margin: 0 0 8px;
    color: #666;
    font-size: 0.95rem;
  }

  .confirm-note {
    font-size: 0.85rem;
    color: #999;
    margin-bottom: 20px;
  }
}

.confirm-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.cancel-btn {
  padding: 10px 20px;
  background: #e9ecef;
  color: #333;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #dee2e6;
  }
}

.delete-confirm-btn {
  padding: 10px 20px;
  background: #dc3545;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #c82333;
  }
}

/* Mobile adjustments */
@media screen and (max-width: 768px) {
  .table-header {
    display: none;
  }

  .table-row {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px;
  }

  .col-name {
    order: 1;
  }

  .col-type,
  .col-context {
    order: 2;
  }

  .col-macros {
    order: 3;
  }

  .col-status {
    order: 4;
  }

  .col-actions {
    order: 5;
    justify-content: flex-end;
  }
}

@media screen and (max-width: 480px) {
  .filters-row {
    flex-direction: column;
  }

  .filter-select {
    width: 100%;
  }

  .manager-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .add-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
